import type { Item, Level, Vec2 } from '../core/hp/types';
import { containsPoint, type Point3 } from './sceneGesture';
import { applyItemProperties } from './itemProperties';
import { isAttic } from '../core/geometry/attic';
export type ResizeAxis = 'w' | 'h' | 'd';
export interface ResizeFace { axis: ResizeAxis; side: -1 | 1 }
export interface ResizeBounds { min: Point3; max: Point3 }
export const MIN_ITEM_SIZE = .05;
const component = { w:'x', h:'y', d:'z' } as const;
export function fallbackResizeBounds(item: Item): ResizeBounds {
  // A mansard's position is its back edge on the wall (see localFootprint).
  if (isAttic(item)) return { min:{x:-item.size.w/2,y:0,z:0}, max:{x:item.size.w/2,y:item.size.h,z:item.size.d} };
  return { min:{x:-item.size.w/2,y:0,z:-item.size.d/2}, max:{x:item.size.w/2,y:item.size.h,z:item.size.d/2} };
}
export function resizeAxisVector(item: Item, axis: ResizeAxis): Point3 {
  const c=Math.cos(item.rotation),s=Math.sin(item.rotation);
  return axis==='h'?{x:0,y:1,z:0}:axis==='w'?{x:c,y:0,z:s}:{x:-s,y:0,z:c};
}
/** Delta is signed distance along the local positive axis, in metres. The opposite
 * physical face stays fixed, including asymmetric authored bounds and signed scale. */
export function resizeItemFace(item: Item, bounds: ResizeBounds, face: ResizeFace, delta: number): Item {
  const key=component[face.axis], span=bounds.max[key]-bounds.min[key];
  if (!Number.isFinite(delta)||!(span>1e-8)) return item;
  // Preserve authored sub-5cm panels: a tangential gesture must not thicken them.
  const minimum=Math.min(MIN_ITEM_SIZE,item.size[face.axis]);
  const size=Math.min(300,Math.max(minimum,item.size[face.axis]*(1+face.side*delta/span)));
  const ratio=size/item.size[face.axis], fixed=face.side===1?bounds.min[key]:bounds.max[key];
  const shift=fixed*(1-ratio), axis=resizeAxisVector(item,face.axis);
  if (size === item.size[face.axis]) return item;
  const result={...item};
  applyItemProperties(result,{size:{...item.size,[face.axis]:size},position:{x:item.position.x+axis.x*shift,z:item.position.z+axis.z*shift},
    elevation:item.elevation+axis.y*shift,rotation:item.rotation});
  return result;
}
export function previewItemResize(level: Level, id: string, bounds: ResizeBounds, face: ResizeFace, delta: number): Level {
  const source=level.items.find(item=>item.id===id); if(!source)return level;
  const corners=(item:Item)=>{
    const rx=item.size.w/source.size.w, rz=item.size.d/source.size.d,c=Math.cos(item.rotation),s=Math.sin(item.rotation);
    return [[bounds.min.x,bounds.min.z],[bounds.max.x,bounds.min.z],[bounds.max.x,bounds.max.z],[bounds.min.x,bounds.max.z]].map(([x,z])=>({x:item.position.x+x*rx*c-z*rz*s,z:item.position.z+x*rx*s+z*rz*c}));
  };
  // Imported placements may deliberately overhang a room; only constrain an already
  // contained footprint. Never translate the anchored face to repair an old project.
  const room=level.rooms.find(room=>footprintInside(corners(source),room.polygon));
  let resized=resizeItemFace(source,bounds,face,delta);
  if(room && !footprintInside(corners(resized),room.polygon)){
    let lo=0,hi=1; for(let i=0;i<40;i++){const mid=(lo+hi)/2, candidate=resizeItemFace(source,bounds,face,delta*mid);
      if(footprintInside(corners(candidate),room.polygon))lo=mid;else hi=mid;}
    resized=resizeItemFace(source,bounds,face,delta*lo);
  }
  if (resized === source) return level;
  const items=level.items.slice();items[items.indexOf(source)]=resized;return {...level,items};
}

function footprintInside(corners:Vec2[],polygon:Vec2[]):boolean {
  if(!corners.every(p=>containsPoint(polygon,p)))return false;
  // Check every interval of each edge between polygon crossings. Corners alone
  // would allow a wide item to bridge a U-shaped room's exterior notch.
  return corners.every((a,index)=>{
    const b=corners[(index+1)%corners.length],dx=b.x-a.x,dz=b.z-a.z,steps=[0,1];
    for(let i=0;i<polygon.length;i++){
      const c=polygon[i],d=polygon[(i+1)%polygon.length],ex=d.x-c.x,ez=d.z-c.z,den=dx*ez-dz*ex;
      if(Math.abs(den)<1e-12)continue;
      const t=((c.x-a.x)*ez-(c.z-a.z)*ex)/den,u=((c.x-a.x)*dz-(c.z-a.z)*dx)/den;
      if(t>0&&t<1&&u>=0&&u<=1)steps.push(t);
    }
    steps.sort((a,b)=>a-b);
    return steps.slice(1).every((t,i)=>{const mid=(t+steps[i])/2;return containsPoint(polygon,{x:a.x+dx*mid,z:a.z+dz*mid});});
  });
}
/** Unity ResizeMarkersBase: closest ray/axis pair; retain the last valid sample when
 * the axis is within ten degrees of the view ray or its closest point is behind it. */
export function axisRayParameter(origin:Point3,direction:Point3,axisOrigin:Point3,axis:Point3):number|null{
  const dot=(a:Point3,b:Point3)=>a.x*b.x+a.y*b.y+a.z*b.z;
  const w={x:origin.x-axisOrigin.x,y:origin.y-axisOrigin.y,z:origin.z-axisOrigin.z};
  const b=dot(direction,axis),den=1-b*b;if(den<Math.sin(Math.PI/18)**2)return null;
  const t=(b*dot(axis,w)-dot(direction,w))/den;if(t<0)return null;
  const u=dot(axis,w)+b*t;return Number.isFinite(u)?u:null;
}
/** Handles closer than `clear` screen pixels are pushed apart, each out along its own axis on screen
 * (`dir`, a unit vector), a few pixels a pass, so every face of a small or distant item keeps a
 * handle a pointer can reach. A moved handle keeps its face's own point in `anchor`, for a leader. */
export function separateHandles<T extends { x: number; y: number; dir: { x: number; y: number }; anchor?: { x: number; y: number } }>(handles: T[], clear: number, step = 3, passes = 24): T[] {
  for (let pass = 0; pass < passes; pass++) {
    let crowded = false;
    for (let i = 0; i < handles.length; i++) for (let j = i + 1; j < handles.length; j++) {
      const a = handles[i], b = handles[j];
      if (Math.hypot(a.x - b.x, a.y - b.y) >= clear) continue;
      crowded = true;
      for (const handle of [a, b]) { handle.anchor ??= { x: handle.x, y: handle.y }; handle.x += handle.dir.x * step; handle.y += handle.dir.y * step; }
    }
    if (!crowded) break;
  }
  return handles;
}
